import { ConfirmDialog } from '@/components/confirm-dialog'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  IconArrowLeft,
  IconBrain,
  IconLoader2,
  IconSparkles,
  IconTool
} from '@tabler/icons-react'
import { Link, useBlocker, useNavigate, useParams } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { ZodError } from 'zod'
import {
  AgentConfigProvider,
  useAgentConfig,
} from '../../context/agent-config-context'
import { agentConfigSchema } from '../../schema'
import { BrainTab } from './brain-tab'
import { PersonalityTab } from './personality-tab'
import { SkillsTab } from './skills-tab'
import { TestPanel } from './test-panel'

function AgentConfigContent() {
  const { id } = useParams({ from: '/_authenticated/agents/$id/edit' })
  const navigate = useNavigate()
  const {
    config,
    agentId,
    saveAgent,
    loadAgent,
    isSaving,
    isLoading,
    setActiveTab,
  } = useAgentConfig()
  const routeAgentId = id && id !== 'new' ? Number.parseInt(id, 10) : null
  const routeSnapshotKey = id ?? 'new'
  const lastRequestedRouteIdRef = useRef<string | null>(null)
  const [savedConfigSnapshot, setSavedConfigSnapshot] = useState<{
    routeId: string
    value: string
  } | null>(null)

  // Load agent data on mount if editing
  useEffect(() => {
    if (routeAgentId === null) {
      lastRequestedRouteIdRef.current = null
      return
    }

    if (
      isLoading ||
      agentId === routeAgentId ||
      lastRequestedRouteIdRef.current === routeSnapshotKey
    ) {
      return
    }

    lastRequestedRouteIdRef.current = routeSnapshotKey
    void loadAgent(routeAgentId)
  }, [agentId, isLoading, loadAgent, routeAgentId, routeSnapshotKey])

  useEffect(() => {
    if (isLoading || savedConfigSnapshot?.routeId === routeSnapshotKey) {
      return
    }

    if (routeAgentId !== null && agentId !== routeAgentId) {
      return
    }

    setSavedConfigSnapshot({
      routeId: routeSnapshotKey,
      value: JSON.stringify(config),
    })
  }, [agentId, config, isLoading, routeAgentId, routeSnapshotKey, savedConfigSnapshot?.routeId])

  const currentConfigSnapshot = useMemo(() => JSON.stringify(config), [config])
  const hasUnsavedChanges =
    savedConfigSnapshot?.routeId === routeSnapshotKey &&
    currentConfigSnapshot !== savedConfigSnapshot.value

  const shouldBlockNavigation = useCallback(
    () => hasUnsavedChanges,
    [hasUnsavedChanges]
  )
  const navigationBlocker = useBlocker({
    shouldBlockFn: shouldBlockNavigation,
    withResolver: true,
    enableBeforeUnload: hasUnsavedChanges,
    disabled: isSaving,
  })

  const validateConfig = (): boolean => {
    try {
      agentConfigSchema.parse(config)
      return true
    } catch (error) {
      if (error instanceof ZodError) {
        // Show toast with first error
        const firstError = error.errors[0]
        if (!firstError) {
          toast.error('Erro de validação no formulário')
          return false
        }
        toast.error(`Erro de validação: ${firstError.message}`)

        // Navigate to tab with error
        const errorField = firstError.path[0] as string
        if (
          [
            'systemPrompt',
            'name',
            'initialMessage',
            'tone',
            'model',
            'language',
            'timezone',
            'responseDelay',
            'companyName',
            'companySector',
            'companyWebsite',
            'companyDescription',
            'stages',
          ].includes(errorField)
        ) {
          setActiveTab('personality')
        } else if (
          ['files', 'websites', 'youtubeLinks', 'qaPairs'].includes(errorField)
        ) {
          setActiveTab('brain')
        } else if (
          ['isActive', 'canRespondAudio', 'audioVoice'].includes(errorField)
        ) {
          setActiveTab('skills')
        }
      }
      return false
    }
  }

  const handleSave = async () => {
    // Validate before saving
    if (!validateConfig()) {
      return
    }

    try {
      await saveAgent()
      setSavedConfigSnapshot({
        routeId: routeSnapshotKey,
        value: JSON.stringify(config),
      })
    } catch {
      // Error is already handled in the context with toast
    }
  }

  const handleConfirmLeave = () => {
    navigationBlocker.proceed?.()
  }

  const handleStayOnPage = () => {
    navigationBlocker.reset?.()
  }

  if (isLoading) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <div className='text-center'>
          <IconLoader2 className='mx-auto mb-4 h-8 w-8 animate-spin text-primary' />
          <p className='text-sm text-muted-foreground'>Carregando agente...</p>
        </div>
      </div>
    )
  }

  return (
    <div className='flex h-screen flex-col'>
      {/* Header */}
      <div className='border-b px-6 py-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-4'>
            <Link to='/agents'>
              <Button variant='ghost' size='icon'>
                <IconArrowLeft className='h-5 w-5' />
              </Button>
            </Link>
            <div>
              <h1 className='text-2xl font-bold'>
                {id === 'new' ? 'Criar Agente' : 'Editar Agente'}
              </h1>
              <p className='text-sm text-muted-foreground'>
                Configure todos os aspectos do seu agente conversacional
              </p>
            </div>
          </div>
          <div className='flex gap-2'>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <IconLoader2 className='mr-2 h-4 w-4 animate-spin' />
                  Salvando...
                </>
              ) : (
                'Salvar Agente'
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1 overflow-hidden'>
        <div className='grid h-full grid-cols-1 gap-0 lg:grid-cols-3'>
          {/* Left Side - Configuration Tabs */}
          <div className='border-r lg:col-span-2'>
            <Tabs defaultValue='personality' className='flex h-full flex-col'>
              <div className='border-b px-6 py-4'>
                <TabsList className='grid w-full grid-cols-3'>
                  <TabsTrigger value='personality' className='gap-2'>
                    <IconSparkles className='h-4 w-4' />
                    <span className='hidden sm:inline'>Personalidade</span>
                  </TabsTrigger>
                  <TabsTrigger value='brain' className='gap-2'>
                    <IconBrain className='h-4 w-4' />
                    <span className='hidden sm:inline'>Cérebro</span>
                  </TabsTrigger>
                  <TabsTrigger value='skills' className='gap-2'>
                    <IconTool className='h-4 w-4' />
                    <span className='hidden sm:inline'>Habilidades</span>
                  </TabsTrigger>
                  {/* <TabsTrigger value='channels' className='gap-2'>
                    <IconDeviceDesktop className='h-4 w-4' />
                    <span className='hidden sm:inline'>Onde Usar</span>
                  </TabsTrigger> */}
                </TabsList>
              </div>

              <div className='flex-1 overflow-hidden px-6 py-4'>
                <TabsContent value='personality' className='m-0 h-full'>
                  <PersonalityTab />
                </TabsContent>
                <TabsContent value='brain' className='m-0 h-full'>
                  <BrainTab />
                </TabsContent>
                <TabsContent value='skills' className='m-0 h-full'>
                  <SkillsTab />
                </TabsContent>
                {/* <TabsContent value='channels' className='m-0 h-full'>
                  <ChannelsTab />
                </TabsContent> */}
              </div>
            </Tabs>
          </div>

          {/* Right Side - Test Panel */}
          <div className='hidden p-6 lg:block'>
            <TestPanel />
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={navigationBlocker.status === 'blocked'}
        onOpenChange={(open) => {
          if (!open) {
            handleStayOnPage()
          }
        }}
        title='Sair sem salvar?'
        desc='Você tem alterações não salvas. Se sair agora, elas serão perdidas.'
        cancelBtnText='Continuar editando'
        confirmText='Sair sem salvar'
        handleConfirm={handleConfirmLeave}
      />
    </div>
  )
}

export function AgentConfigPage() {
  return (
    <AgentConfigProvider>
      <AgentConfigContent />
    </AgentConfigProvider>
  )
}
